import { useEffect, useRef } from 'react';

/**
 * A turntable of the pet, in the appearance tab.
 *
 * ## Why a second renderer, and not the arena's
 *
 * Because a preview must be able to show something the pet is NOT wearing. The
 * arena overlay draws the authoritative pet; if the two shared a renderer, a
 * material set, or even a cached model, selecting a colour to look at would
 * repaint the pet actually flying - which is the one thing a preview must never
 * do. Two renderers means two material sets, and the separation is structural
 * rather than a rule someone has to remember.
 *
 * It costs a second WebGL context, so it exists only while the tab is open and
 * is disposed on the way out.
 *
 * ## What it is not allowed to cost
 *
 * The arena must stay fluid while this is on screen, and the two share one GPU
 * and one main thread. So the turntable is CAPPED at 30 frames per second and
 * stops entirely whenever it is not visible - scrolled out of the panel, tab
 * hidden, or section changed. A close-up that rotates once every two seconds
 * loses nothing at 30 Hz, and the frames it gives back are the ones the pet in
 * the arena needs. The arena's own resolution and quality are never reduced to
 * pay for a preview; the preview yields instead.
 *

 * ## Lazy, like the overlay
 *
 * Three.js is behind the same dynamic `import()`. Opening the appearance tab
 * after the arena has already loaded it costs nothing: the module registry has
 * it, and no second request is made.
 */

/**
 * The turntable's frame budget: 30 per second.
 *
 * Exported so a spec can state the cap it is checking rather than repeating a
 * magic number. It is a CEILING on the preview only - the arena overlay is
 * uncapped and keeps whatever the display offers.
 */
export const PREVIEW_MAX_FPS = 30;
const PREVIEW_FRAME_SECONDS = 1 / PREVIEW_MAX_FPS;

interface PetPreview3DProps {
  /** Which pet to build. Its model module is fetched only when shown. */
  readonly definitionId: string;
  readonly evolution: number;
  readonly energyId: string;
  readonly finishId: string;
  /** True while showing something the pet does not actually wear. */
  readonly previewing: boolean;
  readonly onReset: () => void;
}

export function PetPreview3D({
  definitionId,
  evolution,
  energyId,
  finishId,
  previewing,
  onReset,
}: PetPreview3DProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const wantedRef = useRef({ definitionId, evolution, energyId, finishId });
  wantedRef.current = { definitionId, evolution, energyId, finishId };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    let cancelled = false;
    let frame = 0;
    let teardown: (() => void) | null = null;

    void (async () => {
      try {
        const { PetTurntable } = await import('../game/pet3d/PetTurntable');
        if (cancelled) {
          return;
        }
        const turntable = new PetTurntable(host);
        let loaded = '';
        let last = performance.now();
        /** Time owed to the turntable but not yet drawn, seconds. */
        let owed = 0;
        /*
         * Off screen means OFF, not merely invisible.
         *
         * A canvas scrolled out of the panel still renders unless something
         * stops it, and a second WebGL context drawing a pet nobody can see is
         * the arena's frame budget spent on nothing.
         */
        let onScreen = true;
        const visibility = new IntersectionObserver(
          (entries) => {
            onScreen = entries.some((entry) => entry.isIntersecting);
          },
          { threshold: 0 },
        );
        visibility.observe(host);

        const tick = (now: number) => {
          if (cancelled) {
            return;
          }
          const dt = Math.min((now - last) / 1000, 0.25);
          last = now;
          if (document.hidden || !onScreen) {
            // Paused, not slowed: no draw, and no time accrued either, so
            // coming back does not fast-forward the turntable.
            owed = 0;
            frame = requestAnimationFrame(tick);
            return;
          }
          const wanted = wantedRef.current;
          const key = `${wanted.definitionId}:${wanted.evolution}:${wanted.energyId}:${wanted.finishId}`;
          if (key !== loaded) {
            loaded = key;
            void turntable.setAppearance(
              wanted.definitionId,
              wanted.evolution,
              wanted.energyId,
              wanted.finishId,
            );
          }
          owed += dt;
          if (owed >= PREVIEW_FRAME_SECONDS) {
            // The whole accrued interval is handed over, so the rotation runs
            // at real speed while only a third of the frames are drawn.
            turntable.render(owed);
            owed = 0;
          }
          frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        teardown = () => {
          visibility.disconnect();
          turntable.dispose();
        };
      } catch {
        // No WebGL, or the chunk failed: the swatches still work, and the
        // player simply does not get a turntable. Never fatal.
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      teardown?.();
    };
  }, []);

  return (
    <section className="pet-preview" data-testid="pet-preview-section">
      <div className="pet-preview-head">
        <h4>Prévia</h4>
        <span
          className="pet-preview-state"
          data-previewing={previewing ? 'true' : 'false'}
          data-testid="pet-preview-state"
        >
          {previewing ? 'PRÉVIA' : 'APLICADO'}
        </span>
        {previewing ? (
          <button type="button" data-testid="pet-preview-reset" onClick={onReset}>
            VER O ATUAL
          </button>
        ) : null}
      </div>
      <div ref={hostRef} className="pet-preview-stage" data-testid="pet-preview-stage" />
    </section>
  );
}
